import api from '@/lib/axios'

// ============================================================================
//  treasury.api.ts — Couche API Trésorerie
//  Consomme /api/v1/treasury (TreasuryController — Phase 2)
// ============================================================================

export interface BankAccount {
  id: string
  name: string
  bankName: string
  accountNumber: string
  iban?: string
  swift?: string
  currency: string
  currentBalance: number
  openingBalance: number
  lowBalanceAlert?: number
  accountingCode?: string
  isActive: boolean
  _count?: { transactions: number; bankStatements: number }
}

export interface BankAccountBalance {
  accountId: string
  accountName: string
  bankName: string
  currentBalance: number
  currency: string
  totalReceipts: number
  totalPayments: number
  pendingCount: number
  lowBalanceAlert: boolean
}

export interface CashTransaction {
  id: string
  bankAccountId: string
  type: 'RECEIPT' | 'PAYMENT' | 'TRANSFER_IN' | 'TRANSFER_OUT' | 'FEE' | 'INTEREST'
  amount: number
  currency: string
  date: string
  valueDate?: string
  reference?: string
  description: string
  thirdPartyName?: string
  status: 'PENDING' | 'RECONCILED' | 'CANCELLED'
  reconciledAt?: string
  journalEntryId?: string
  bankAccount?: { name: string; bankName: string; currency: string }
  createdAt: string
}

export interface BankStatement {
  id: string
  bankAccountId: string
  statementDate: string
  startDate: string
  endDate: string
  openingBalance: number
  closingBalance: number
  isReconciled: boolean
  reconciledAt?: string
  transactions: CashTransaction[]
}

export interface TreasuryForecast {
  id: string
  bankAccountId: string
  type: string
  amount: number
  expectedDate: string
  description: string
  category?: string
  probability: number
  isActualized: boolean
  actualAmount?: number
  actualDate?: string
  bankAccount?: { name: string; bankName: string }
}

export interface CashFlowForecast {
  currentBalance: number
  forecastDays: number
  dailyFlow: Array<{
    date: string
    receipts: number
    payments: number
    netFlow: number
    cumulativeBalance: number
  }>
  minBalance: number
  maxBalance: number
  criticalDays: number
  alerts: Array<{ date: string; balance: number }>
  currency: string
}

// ─── Types pagination alignés sur le format réel du backend ──────────────────
// Le backend retourne { transactions, total, page, limit, pages }

export interface TransactionsPage {
  transactions: CashTransaction[]
  total: number
  page: number
  limit: number
  pages: number
}

// ─── API ─────────────────────────────────────────────────────────────────────

export const treasuryApi = {
  // ── Comptes bancaires ──────────────────────────────────────────────────────

  getBankAccounts: (includeInactive?: boolean) =>
    api
      .get<BankAccount[]>('/treasury/bank-accounts', { params: { includeInactive } })
      .then((r) => r.data?.data ?? r.data),

  getBankAccount: (id: string) =>
    api.get<BankAccount>(`/treasury/bank-accounts/${id}`).then((r) => r.data?.data ?? r.data),

  createBankAccount: (data: {
    name: string
    bankName: string
    accountNumber: string
    iban?: string
    swift?: string
    currency?: string
    openingBalance?: number
    lowBalanceAlert?: number
    accountingCode?: string
  }) => api.post<BankAccount>('/treasury/bank-accounts', data).then((r) => r.data?.data ?? r.data),

  updateBankAccount: (id: string, data: Partial<BankAccount>) =>
    api
      .patch<BankAccount>(`/treasury/bank-accounts/${id}`, data)
      .then((r) => r.data?.data ?? r.data),

  getBankAccountBalance: (id: string, params?: { startDate?: string; endDate?: string }) =>
    api
      .get<BankAccountBalance>(`/treasury/bank-accounts/${id}/balance`, { params })
      .then((r) => r.data?.data ?? r.data),

  // ── Transactions ───────────────────────────────────────────────────────────

  getTransactions: (params?: {
    bankAccountId?: string
    status?: string
    startDate?: string
    endDate?: string
    page?: number
    limit?: number
  }) =>
    api
      .get<TransactionsPage>('/treasury/transactions', { params })
      .then((r) => r.data?.data ?? r.data),

  createTransaction: (data: {
    bankAccountId: string
    type: string
    amount: number
    date: string
    description: string
    reference?: string
    thirdPartyName?: string
  }) =>
    api.post<CashTransaction>('/treasury/transactions', data).then((r) => r.data?.data ?? r.data),

  cancelTransaction: (id: string, reason: string) =>
    api.post(`/treasury/transactions/${id}/cancel`, { reason }).then((r) => r.data?.data ?? r.data),

  // ── Rapprochement ──────────────────────────────────────────────────────────

  importStatement: (data: {
    bankAccountId: string
    statementDate: string
    startDate: string
    endDate: string
    openingBalance: number
    closingBalance: number
    transactions: Array<{
      date: string
      type: string
      amount: number
      description: string
      reference?: string
    }>
  }) =>
    api
      .post<BankStatement>('/treasury/statements/import', data)
      .then((r) => r.data?.data ?? r.data),

  reconcileTransaction: (id: string, journalEntryId?: string) =>
    api
      .post(`/treasury/transactions/${id}/reconcile`, { journalEntryId })
      .then((r) => r.data?.data ?? r.data),

  autoReconcile: (bankAccountId: string, tolerance?: number) =>
    api
      .post(`/treasury/bank-accounts/${bankAccountId}/auto-reconcile`, { tolerance })
      .then((r) => r.data?.data ?? r.data),

  closeStatement: (id: string) =>
    api.post(`/treasury/statements/${id}/close`).then((r) => r.data?.data ?? r.data),

  // ── Prévisions ─────────────────────────────────────────────────────────────

  getCashFlowForecast: (days?: number, bankAccountId?: string) =>
    api
      .get<CashFlowForecast>('/treasury/forecast', { params: { days, bankAccountId } })
      .then((r) => r.data?.data ?? r.data),

  getForecasts: (params?: { bankAccountId?: string; fromDate?: string; toDate?: string }) =>
    api
      .get<TreasuryForecast[]>('/treasury/forecast/items', { params })
      .then((r) => r.data?.data ?? r.data),

  createForecast: (data: {
    bankAccountId: string
    type: string
    amount: number
    expectedDate: string
    description: string
    probability?: number
  }) =>
    api
      .post<TreasuryForecast>('/treasury/forecast/items', data)
      .then((r) => r.data?.data ?? r.data),

  actualizeForecast: (id: string, actualAmount: number, actualDate: string) =>
    api
      .patch(`/treasury/forecast/items/${id}/actualize`, { actualAmount, actualDate })
      .then((r) => r.data?.data ?? r.data),
}
